<template>
  <div
    :class="{ 'war-room-comment-author': true, 'war-room-comment-author__disabled': !profileUrl }"
    data-test="author-link"
    @click="handleClick"
  >
    <Avatar
      v-if="author"
      class="war-room-comment-author__avatar"
      type="next-tiny"
      :alt="`${username} profile picture`"
      :title="initials"
      :src="author.avatar_url"
      loading="lazy"
    />
    <BravadoText class="war-room-comment-author__title" variant="subhead-semibold" tag="h4">
      {{ username }}
    </BravadoText>
    <div v-if="achievement" class="war-room-comment-author__achievements">
      <WarRoomAchievement :title="achievement.title" :level="achievement.level" :size="achievementSize" />
      <WarRoomAchievement
        v-if="additionalAchievement"
        :title="additionalAchievement.title"
        :level="additionalAchievement.level"
        :size="achievementSize"
      />
    </div>
    <BravadoText v-if="position" class="war-room-comment-author__position" variant="subhead" tag="p">
      {{ position }}
    </BravadoText>
    <div v-if="!profileUrl" class="war-room-comment-author__hint">
      <BravadoText
        v-if="disabledLinkHint"
        class="war-room-comment-author__hint-text"
        tag="p"
        variant="subhead-semibold"
      >
        {{ disabledLinkHint }}
      </BravadoText>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, useContext, useRouter } from '@nuxtjs/composition-api';
import WarRoomAchievement from '@/components/partials/war-room/WarRoomAchievement.vue';
import Avatar from '@/components/common/Avatar/Avatar.vue';
import { achievementSizes } from '@/components/partials/war-room/constants';
import { initials as initialsUtil } from '@/components/utils/core/string';
import type { IPostUser } from '@/types/community';

type WarRoomCommentAuthorProps = {
  author?: IPostUser;
};

const props = withDefaults(defineProps<WarRoomCommentAuthorProps>(), {
  author: () => ({}),
});

const router = useRouter();
const { $auth } = useContext();

const achievementSize = achievementSizes.SMALL;

const profileUrl = computed(() => {
  if (!props.author) {
    return;
  }
  if (props.author.username) {
    return `/war-room/profile/${props.author.username}`;
  }
  if (props.author.portfolio_configuration?.is_public || props.author.slug === $auth.user?.slug) {
    return `/p/${props.author.slug}`;
  }
});

const username = computed(() => {
  if (!props.author) {
    return 'Deactivated User';
  }
  if (!profileUrl.value && Boolean(props.author)) {
    return `${props.author.firstname} ${props.author.lastname?.substring(0, 1)}`;
  }
  return props.author.username ?? `${props.author.firstname} ${props.author.lastname}`;
});
const initials = computed(() => initialsUtil(username.value));
const disabledLinkHint = computed(() => {
  if (!props.author) {
    return;
  }
  if (props.author.firstname) {
    return `${props.author.firstname.trim()}'s profile is not public`;
  }

  return `Author's profile is not public`;
});

const position = computed(() => {
  if (!props.author?.position) return '';

  let position = props.author.position;

  if (props.author.current_company) {
    position += ` at ${props.author.current_company}`;
  }

  return position;
});

const achievement = computed(() => {
  if (props.author?.achievements?.length) return props.author.achievements[0];
});

const additionalAchievement = computed(() => {
  const remainingCount = Math.max(0, props.author?.achievements?.length - 1);
  if (remainingCount < 1) return undefined;
  return {
    title: `+${remainingCount}`,
    slug: '',
    level: 'no_level',
  };
});

/**
 * Profile opens by js and not a regular link on purpose.
 * For SEO number of outgoing links should be limited, because page shares link juice between them.
 * We block profile from indexing, so don't need to have a link here.
 * If we need to index profiles, please add a link here.
 */
function handleClick() {
  if (profileUrl.value) router.push(profileUrl.value);
}
</script>

<style scoped lang="scss">
.war-room-comment-author {
  $this: &;

  &__disabled {
    position: relative;

    &:hover,
    &:focus {
      cursor: default !important;

      #{$this}__title {
        text-decoration: none !important;
      }

      #{$this}__hint {
        display: block;
      }
    }
  }

  display: flex;
  align-items: center;
  width: 100%;
  outline: none;

  &:hover,
  &:focus {
    cursor: pointer;

    #{$this}__title {
      text-decoration: underline;
    }
  }

  &__avatar {
    margin-right: 8px;
  }

  &__title {
    color: var(--theme-color-text);
  }

  &__achievements {
    display: flex;
    align-items: center;
    margin-left: 8px;
    grid-gap: 4px;
  }

  &__position {
    display: none;

    @include mq('sm') {
      display: block;
      position: relative;
      color: var(--theme-color-sub-text);
      padding-left: 16px;

      &::before {
        content: '';
        position: absolute;
        top: calc(50% - 2px);
        left: 7px;
        width: 2px;
        height: 2px;
        background-color: var(--theme-color-sub-text);
        border-radius: 50%;
      }
    }
  }

  &__hint {
    display: none;
    position: absolute;
    padding-top: 6px;
    top: calc(100% + 6px);
    z-index: 99;
    left: 0;

    &-text {
      position: relative;
      padding: 8px;
      color: var(--theme-color-card);
      background-color: var(--theme-color-text);
      border-radius: 8px;

      &::before {
        content: '';
        position: absolute;
        left: 4px;
        top: -2px;
        width: 10px;
        height: 10px;
        transform: rotate(45deg);
        background-color: var(--theme-color-text);
      }
    }
  }
}
</style>
